import { Card, Flex, Typography } from 'antd'
import React, { ReactNode } from 'react'
import { styled } from 'styled-components'
import { Icon } from '~/features/shared/icons'

const Wrapper = styled(Card)<{ $isActive: boolean }>`
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;

  ${props => {
    if (props.$isActive) {
      return `
        background-color: #fff9e5;
        border: 2px solid #FFCC01;
        box-shadow: 0 0 0 2px rgba(255, 204, 1, 0.25);
      `
    } else {
      return `
        background-color: #fff;
        border: 2px solid transparent;
        box-shadow: 0 0 0 1px #e0e0e0, 0 1px 4px rgba(0, 0, 0, 0.05);
      `
    }
  }}

  &:hover {
    ${props => {
      if (props.$isActive) {
        return `
          box-shadow: 0 0 0 2px rgba(255, 204, 1, 0.4);
          transform: translateY(-1px);
        `
      } else {
        return `
          background-color: #fafafa;
          border: 2px solid #f0f0f0;
          box-shadow: 0 0 0 1px #d0d0d0, 0 2px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-1px);
        `
      }
    }}
  }

  &:active {
    transform: translateY(0px);
    ${props => {
      if (props.$isActive) {
        return `
          box-shadow: 0 0 0 2px rgba(255, 204, 1, 0.15);
        `
      } else {
        return `
          background-color: #f5f5f5;
          box-shadow: 0 0 0 1px #d0d0d0, 0 1px 2px rgba(0, 0, 0, 0.1);
        `
      }
    }}
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

type Option = {
  label: ReactNode
  description?: ReactNode
  value: string
  img?: string
  onClick?: () => void
}

type Props = {
  id?: string
  value?: string
  initialValues?: string
  options: Option[]
  grid?: boolean // New prop to enable grid layout
  onChange?: (value: string) => void
}

export const RadioCard: React.FC<Props> = ({
  id,
  value,
  options,
  grid = false,
  onChange,
}) => {
  const renderCard = (option: Option) => {
    const { label, description, img } = option

    return (
      <Wrapper
        key={option.value}
        $isActive={value === option.value}
        onClick={() => {
          option.onClick?.()
          onChange(option.value)
        }}
      >
        <Flex align="center" justify="space-between" gap={24}>
          <Flex gap={15} align="center">
            {img && (
              <>
                <img
                  src={img}
                  alt={label as string}
                  style={{ width: '40px', height: '40px' }}
                />
              </>
            )}
            <Flex vertical>
              <Typography.Text strong style={{ fontSize: '16px' }}>
                {label}
              </Typography.Text>

              {description && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: '14px', fontWeight: 'normal' }}
                >
                  {description}
                </Typography.Text>
              )}
            </Flex>
          </Flex>

          <Typography.Text strong style={{ fontSize: '20px' }}>
            <Icon name="chevron-right" />
          </Typography.Text>
        </Flex>
      </Wrapper>
    )
  }

  if (grid) {
    return (
      <GridContainer id={id}>
        {options.map(option => renderCard(option))}
      </GridContainer>
    )
  }

  return (
    <Flex vertical gap={8} id={id}>
      {options.map(option => renderCard(option))}
    </Flex>
  )
}
